import { FC, useEffect, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, MultiSelectChip, MaterialSymbol, withStyles } from 'vopay';
import { Button } from 'UI';
import type { AccountUserRole, UserRoleAssignment } from 'vopay';
import useFetchUserRoleAssignments from '@/hooks/useApi/useFetchUserRoleAssignments';
import useAssignUserRole from '@/hooks/useApi/useAssignUserRole';
import useUnassignUserRole from '@/hooks/useApi/useUnassignUserRole';
import { useToast } from '@/providers/Toast.provider';
import styles from './SelectRoleDialogue.module.scss';

const cn = withStyles(styles);

type SelectRoleDialogueProps = {
  isOpen: boolean;
  user: { Username: string } | null;
  availableRoles: AccountUserRole[];
  onClose: () => void;
  onAssignmentsChanged?: () => void;
  onCreateNewRole?: () => void;
};

type RoleOption = { value: string; label: string };

const SelectRoleDialogue: FC<SelectRoleDialogueProps> = ({
  isOpen,
  user,
  availableRoles,
  onClose,
  onAssignmentsChanged,
  onCreateNewRole,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const toast = useToast();
  const { fetching, fetchUserRoleAssignments } = useFetchUserRoleAssignments();
  const { submitting: assigning, assignUserRole } = useAssignUserRole();
  const { submitting: unassigning, unassignUserRole } = useUnassignUserRole();

  const [initialRoleIds, setInitialRoleIds] = useState<number[]>([]);
  const [pendingRoleIds, setPendingRoleIds] = useState<number[]>([]);
  // Local in-flight flag covering the entire Confirm batch. The per-hook
  // `submitting` booleans flip false after their first concurrent call
  // resolves while siblings are still pending — which would re-enable the
  // Confirm button mid-batch and let the admin fire duplicate assigns.
  const [isConfirming, setIsConfirming] = useState(false);

  // Load the user's current role assignments each time the dialog opens.
  // Deps are intentionally limited to isOpen and user.Username. The
  // fetchUserRoleAssignments function uses the Direct Pattern (no useCallback)
  // so it recreates on every render — including it would cause infinite loops.
  // The bearer token is captured at call time inside the hook, not via closure.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen || !user) return;
    fetchUserRoleAssignments(user.Username)
      .then((res) => {
        // useFetchUserRoleAssignments returns the raw response; flatten the
        // JSON_FORCE_OBJECT shape AND coerce RoleID to a number (backend
        // serializes it as a string here). Without the Number() cast, the
        // diff in handleConfirm would treat existing role IDs as new ones
        // because of strict-equality type mismatch.
        const rawRoles = res.Roles as unknown;
        const list = Array.isArray(rawRoles)
          ? (rawRoles as UserRoleAssignment[])
          : Object.values((rawRoles ?? {}) as Record<string, UserRoleAssignment>);
        const ids = list.map((r) => Number(r.RoleID));
        setInitialRoleIds(ids);
        setPendingRoleIds(ids);
      })
      .catch(() => {
        setInitialRoleIds([]);
        setPendingRoleIds([]);
      });
  }, [isOpen, user?.Username]);

  // For visual disable states on inputs/chips during writes. The Confirm
  // button uses `isConfirming` instead (covers the whole batch correctly).
  const submitting = assigning || unassigning;
  const options: RoleOption[] = availableRoles.map((role) => ({
    value: String(role.RoleID),
    label: role.Name,
  }));
  const selectedValues = pendingRoleIds.map(String);

  function handleCancel() {
    setPendingRoleIds(initialRoleIds);
    onClose();
  }

  function handleChange(
    _event: ChangeEvent<HTMLInputElement>,
    args: { options: RoleOption[] },
  ) {
    setPendingRoleIds(args.options.map((opt) => Number(opt.value)));
  }

  function handleDelete(value: string) {
    setPendingRoleIds((prev) => prev.filter((id) => String(id) !== value));
  }

  async function handleConfirm() {
    if (!user) return;
    const added = pendingRoleIds.filter((id) => !initialRoleIds.includes(id));
    const removed = initialRoleIds.filter((id) => !pendingRoleIds.includes(id));

    if (added.length === 0 && removed.length === 0) {
      onClose();
      return;
    }

    setIsConfirming(true);
    try {
      await Promise.all([
        ...added.map((RoleID) => assignUserRole({ Username: user.Username, RoleID })),
        ...removed.map((RoleID) => unassignUserRole({ Username: user.Username, RoleID })),
      ]);

      if (added.length > 0 && removed.length === 0) {
        toast.success(t('SelectRole.toast.roleAssigned', { defaultValue: 'Role Assigned' }));
      } else if (removed.length > 0 && added.length === 0) {
        toast.success(t('SelectRole.toast.roleUnassigned', { defaultValue: 'Role Unassigned' }));
      } else {
        toast.success(t('SelectRole.toast.rolesUpdated', { defaultValue: 'Roles updated' }));
      }

      onAssignmentsChanged?.();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      toast.neutral(
        t('SelectRole.toast.error', { defaultValue: `Could not update role assignments: ${message}` }),
      );
    } finally {
      setIsConfirming(false);
    }
  }

  if (!user) return null;

  return (
    <Modal isVisible={isOpen} enableBgClick={false}>
      <div className={cn('body')}>
        <MaterialSymbol icon="groups" fillAmount={1} size="40px" className={cn('icon')} />
        <h2 className={cn('title')}>
          {t('SelectRole.title', { defaultValue: 'Edit Role' })}
        </h2>
        <p className={cn('subtitle')}>
          {t('SelectRole.subtitle', { defaultValue: 'Select all the applicable role(s) to ' })}
          <span className={cn('subtitleEmail')}>{user.Username}</span>
        </p>
        <MultiSelectChip
          name="selectRoleMultiSelect"
          options={options}
          value={selectedValues}
          onChange={handleChange}
          onDelete={handleDelete}
          placeholder={t('SelectRole.placeholder', { defaultValue: 'Select' })}
          filterable
          emptyFilterMsg={t('SelectRole.noRolesFound', { defaultValue: 'No roles found' })}
          disabled={fetching || submitting}
          renderOption={(o, { Option, SelectedIcon, selected }) => (
            <Option>
              {o.label}
              {selected && <SelectedIcon />}
            </Option>
          )}
          renderDropdownFooter={
            onCreateNewRole
              ? ({ close }) => (
                  <Button
                    iconLeft="add"
                    style={{ width: '100%' }}
                    onClick={() => {
                      close();
                      onCreateNewRole();
                    }}
                  >
                    {t('SelectRole.newRole', { defaultValue: 'New Role' })}
                  </Button>
                )
              : undefined
          }
        />
        <div className={cn('footer')}>
          <Button colorScheme="secondary" onClick={handleCancel} disabled={isConfirming}>
            {tGlobal('cancel')}
          </Button>
          <Button colorScheme="primary" onClick={handleConfirm} disabled={isConfirming || fetching}>
            {tGlobal('confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SelectRoleDialogue;
